import { DO_NOT_DISPLAY_THREAD } from 'copilot-toolkit/constants/PromptThread';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INTERACTIVE_TOOL, INTERACTIVE_TOOL_BUTTON_CLICK } from '../constants/Tools';
import { TOPIC_NAMES } from '../types/Channel';
import { useFunctionQueue, processInstantActions, processButtonClick } from '../utils/interactiveModeUtils';
import { safeParse } from '../utils/jsonSchemaUtils';
import { useCollaborationSession } from './useCollaborationSession';
import { useSendMessageFromAppToCopilot } from './useSendCollaboration';
import { useSendOnCopilotChannel } from '../channel/hooks/useSendOnCopilotChannel';
import { useIsCopilotWidgetActive } from './useIsCopilotWidgetActive';
export function useInteractiveModeSession({
  onCopilotAction,
  // Currently not used, but required by useCollaborationSession - but may be used in the future, if we starting using threads
  // instead of directly calling actions.
  onGetCurrentSessionContext = () => {},
  sessionId,
  appName,
  interactiveModeActions,
  additionalLLMInstructions,
  starterActions,
  ignorePathChanges = false,
  defaultToolSet,
  additionalToolSets = [],
  systemConfig,
  aviatorAgentName,
  assetId,
  snapshot
}) {
  const sendMessageFromAppToCopilot = useSendMessageFromAppToCopilot();
  const sendAddStarterActions = useSendOnCopilotChannel(TOPIC_NAMES.ADD_STARTER_ACTIONS);
  const isCopilotWidgetActive = useIsCopilotWidgetActive();
  const {
    addToQueue
  } = useFunctionQueue(appName);
  // CAPTURED STATES FLOW: Store original values before auto-applied actions execute
  // Format: { functionName: { param1: value1, param2: value2 } }
  // This data gets sent to plugins via collaboration state so undo buttons work correctly
  const capturedStatesRef = useRef({});
  const [snapshotValuesByUniqueId, setSnapshotValuesByUniqueId] = useState({});
  const onSnapshotReturned = useCallback((uniqueKey, snapshotValue) => {
    setSnapshotValuesByUniqueId(prev => Object.assign({}, prev, {
      [uniqueKey]: snapshotValue
    }));
  }, []);

  // Package up all the actions and settings the LLM needs to know about
  const customFEActions = useMemo(() => {
    const interactions = interactiveModeActions || [];
    return {
      interactions,
      additionalLLMInstructions: additionalLLMInstructions || '',
      pageName: 'Interactive Mode',
      pageDescription: 'Interactive mode actions',
      ignorePathChanges,
      defaultToolSet,
      additionalToolSets,
      systemConfig,
      aviatorAgentName,
      assetId,
      snapshot
    };
  }, [interactiveModeActions, additionalLLMInstructions, ignorePathChanges, defaultToolSet, additionalToolSets, systemConfig, aviatorAgentName, assetId, snapshot]);
  const handleCopilotAction = useCallback(action => {
    var _action$payload;
    if (onCopilotAction) {
      onCopilotAction(action);
    }
    switch (action === null || action === void 0 || (_action$payload = action.payload) === null || _action$payload === void 0 ? void 0 : _action$payload.actionType) {
      case INTERACTIVE_TOOL:
        {
          var _safeParse, _action$payload$messa, _action$payload2;
          const llmDeterminedActions = (_safeParse = safeParse((_action$payload$messa = action === null || action === void 0 || (_action$payload2 = action.payload) === null || _action$payload2 === void 0 || (_action$payload2 = _action$payload2.message) === null || _action$payload2 === void 0 || (_action$payload2 = _action$payload2.agentPayload) === null || _action$payload2 === void 0 ? void 0 : _action$payload2.jsonContent) !== null && _action$payload$messa !== void 0 ? _action$payload$messa : '')) === null || _safeParse === void 0 ? void 0 : _safeParse.feInteractionsToExecute;

          // Execute actions immediately without showing buttons
          // This will populate capturedStatesRef with original values for undo
          processInstantActions({
            actions: llmDeterminedActions,
            customFEActions,
            capturedStatesRef,
            // Gets populated with original states
            addToQueue,
            sessionId,
            usageTracker: action === null || action === void 0 ? void 0 : action.usageTracker,
            onSnapshotReturned
          });
        }
        break;
      case INTERACTIVE_TOOL_BUTTON_CLICK:
        // User clicked Apply or Undo button - execute the requested action
        processButtonClick(action, customFEActions, addToQueue, onSnapshotReturned, sessionId);
        break;
      default:
        break;
    }
  }, [addToQueue, customFEActions, onCopilotAction, sessionId, onSnapshotReturned]);

  // Set up communication with Copilot and handle incoming messages
  const {
    sendActionFromAppToCopilot,
    sendCollaborationStateChangeFromAppToCopilot,
    isCopilotReady
  } = useCollaborationSession({
    onCopilotAction: handleCopilotAction,
    onGetCurrentSessionContext,
    sessionId,
    appName,
    plugin: 'InteractiveModePlugin'
  });

  // Send suggested starter prompts to Copilot when widget becomes active
  useEffect(() => {
    if (starterActions && isCopilotWidgetActive) {
      sendAddStarterActions(starterActions);
    }
  }, [starterActions, sendAddStarterActions, isCopilotWidgetActive]);

  // CAPTURED STATES FLOW: Send captured states to plugins so undo buttons work correctly
  useEffect(() => {
    if (customFEActions) {
      // Only include captured states if we have any (for undo functionality)
      const hasCaptures = Object.keys(capturedStatesRef.current).length > 0;
      const hasSnapshots = Object.keys(snapshotValuesByUniqueId).length > 0;
      const stateUpdate = Object.assign({
        customFEActions
      }, hasCaptures && {
        capturedStates: capturedStatesRef.current
      }, hasSnapshots && {
        snapshotValuesByUniqueId
      });
      sendCollaborationStateChangeFromAppToCopilot(stateUpdate);
    }
  }, [customFEActions, sendCollaborationStateChangeFromAppToCopilot, capturedStatesRef, snapshotValuesByUniqueId]);

  // Opens Copilot in Interactive Mode for this page
  const openInteractiveMode = useCallback(() => {
    // TODO: use new hook james made to open.
    sendActionFromAppToCopilot({
      title: 'New thread',
      data: {
        intent: 'text_generate',
        prompt: 'FE_ACTIONS'
      },
      promptData: {
        promptPrefix: DO_NOT_DISPLAY_THREAD,
        commandName: 'customFEAction',
        path: []
      }
    });
  }, [sendActionFromAppToCopilot]);

  // Send a text message to Copilot from the application
  const handleSendMessageFromAppToCopilot = useCallback(({
    messageText
  }) => {
    sendMessageFromAppToCopilot({
      messageText,
      messageMetaInfo: {
        source: 'PLUGIN'
      }
    });
  }, [sendMessageFromAppToCopilot]);
  return {
    sendMessageFromAppToCopilot: handleSendMessageFromAppToCopilot,
    isCopilotReady,
    openInteractiveMode
  };
}